/**
 * Roolikohtaiset käyttöoikeudet — iteraatio K5 (10.6.2026).
 *
 * MALLI: Perustyö (sisäkkäinen tikapuu) + Lisäoikeudet (ortogonaalisia).
 *
 *   Perustyö: ei ⊂ aspa ⊂ kasittelija ⊂ admin
 *   Lisäoikeudet: raportit, myonto (vapaasti yhdisteltäviä perustyön kanssa)
 *
 * Efektiiviset oikeudet = union(perustyo-oikeudet ∪ lisaoikeus-oikeudet).
 * Admin = superset kaikesta; admin-profiilissa lisaoikeudet sisältyvät
 * automaattisesti eikä niitä tarvitse erikseen asettaa.
 *
 * Tuotannon oikeusmalli on union (additiivinen RBAC, UX-tutkimus 10.6.2026
 * kohta 2). Proto noudattaa samaa laskentaa. Demo-roolinvalitsin
 * sivupalkissa on UI-tason demoväline (näyttää miltä portaali näyttää
 * kullakin profiililla), ei binding tuotantoon.
 */

import { TrivoreKayttaja } from "../types";

// ---------------------------------------------------------------------------
// Tyypit
// ---------------------------------------------------------------------------

/** Perustyön hierarkkinen taso. Kukin taso sisältää kaikki alemmat. */
export type Perustyo = "ei" | "aspa" | "kasittelija" | "admin";

/** Ortogonaaliset lisäoikeudet, vapaasti yhdisteltäviä perustyön kanssa.
 *  Admin-profiilissa nämä sisältyvät automaattisesti (ei tarkisteta erikseen). */
export interface Lisaoikeudet {
  raportit: boolean; // tukipyyntö- + käyttöraportti
  myonto: boolean; // käyttöoikeuksien myöntämisnäkymä
}

/** Yhden käyttäjän rooliprofiili. */
export interface RooliProfiili {
  perustyo: Perustyo;
  lisaoikeudet: Lisaoikeudet;
}

// ---------------------------------------------------------------------------
// Toiminnot
// ---------------------------------------------------------------------------
const piilotaRaportit = import.meta.env.VITE_PIILOTA_RAPORTIT === "true";
//lisätään myöhemmin tämä:
// | "kayttajat.lue_muistiinpanot"
// | "kayttajat.muokkaa_muistiinpanoja"
export type OriginalToiminto =
  // Päänavigaatio
  | "navi.tukipyynnot"
  | "navi.kayttajahaku"
  | "navi.raportit"
  | "navi.kayttooikeudet" // K5: uusi käyttöoikeusnäkymä
  // Tukipyynnöt
  | "tukipyynnot.avaa_yksittainen"
  | "tukipyynnot.kasittele" // ota itselle, vastaa, ratkaise
  // Käyttäjäkortti
  | "kayttajat.avaa_kortti"
  | "kayttajat.nae_tukipyyntolista" // G2 (22.6.2026): käyttäjän tukipyyntöjen tilat read-only (aspa)
  | "kayttajat.muokkaa_perustietoja"
  | "kayttajat.lukitse_tunnus"
  | "kayttajat.vapauta_tunnus_lukitus"
  | "kayttajat.vapauta_ip_lukitus"
  | "kayttajat.todenna_manuaalisesti"
  | "kayttajat.taikanappi" // K5 3g: ohita ID-proofing virkailijan päätöksellä
  | "kayttajat.poista"
  | "kayttajat.audit_trail" // K5: myös käsittelijälle (OSA 2.3)
  | "kayttajat.tapahtumat_yritykset" // K5: myös käsittelijälle (OSA 2.3)
  // Raportit
  | "raportit.tukipyyntoraportti"
  | "raportit.kayttoraportti"
  | "raportit.kaikkien_kasittelijat"
  // Käyttöoikeudet
  | "kayttooikeudet.myonna" // K5: myöntökortin tallentaminen
  // Misc
  | "jonossa_tagi";

// Conditional type to exclude `raportit.*` if `piilotaRaportit` is true
export type Toiminto = typeof piilotaRaportit extends true
  ? Exclude<
      OriginalToiminto,
      | "navi.raportit"
      | "raportit.tukipyyntoraportti"
      | "raportit.kayttoraportti"
      | "raportit.kaikkien_kasittelijat"
    >
  : OriginalToiminto;

// ---------------------------------------------------------------------------
// Ihmisluettavat nimet toiminnoille (DemoBanner + myöntönäkymä)
// Lisätään myöhemmin nämä:
// "kayttajat.lue_muistiinpanot": "Sisäiset muistiinpanot (luku)",
// "kayttajat.muokkaa_muistiinpanoja": "Sisäiset muistiinpanot (kirjoitus)",
// ---------------------------------------------------------------------------

export const TOIMINTO_NIMET = (
  piilotaRaportit
    ? {
        "navi.tukipyynnot": "Tukipyyntöjen navigointi",
        "navi.kayttajahaku": "Käyttäjähaku",
        "navi.kayttooikeudet": "Käyttöoikeusnäkymä",
        "tukipyynnot.avaa_yksittainen": "Avaa tukipyyntö",
        "tukipyynnot.kasittele": "Käsittele tukipyyntö (ota itselle, ratkaise)",
        "kayttajat.avaa_kortti": "Käyttäjäkortti (katselu)",
        "kayttajat.nae_tukipyyntolista": "Käyttäjän tukipyyntöjen tilat (luku)",
        "kayttajat.muokkaa_perustietoja": "Käyttäjän perustietojen muokkaus",
        "kayttajat.lukitse_tunnus": "Lukitse käyttäjätunnus",
        "kayttajat.vapauta_tunnus_lukitus": "Vapauta käyttäjätunnuksen lukitus",
        "kayttajat.vapauta_ip_lukitus": "Vapauta ID-proofing-lukitus",
        "kayttajat.todenna_manuaalisesti":
          "Todenna henkilöllisyys manuaalisesti",
        "kayttajat.taikanappi": "Ohita ID-proofing (poikkeustoiminto)",
        "kayttajat.poista": "Poista käyttäjä",
        "kayttajat.audit_trail": "Virkailijan toimenpiteet -loki",
        "kayttajat.tapahtumat_yritykset": "Tapahtumat + todentamisyritykset",

        "kayttooikeudet.myonna": "Käyttöoikeuksien myöntäminen",
        jonossa_tagi: '"Tukipyyntöjä jonossa" -tagi',
      }
    : {
        "navi.tukipyynnot": "Tukipyyntöjen navigointi",
        "navi.kayttajahaku": "Käyttäjähaku",
        "navi.raportit": "Raportit",
        "navi.kayttooikeudet": "Käyttöoikeusnäkymä",
        "tukipyynnot.avaa_yksittainen": "Avaa tukipyyntö",
        "tukipyynnot.kasittele": "Käsittele tukipyyntö (ota itselle, ratkaise)",
        "kayttajat.avaa_kortti": "Käyttäjäkortti (katselu)",
        "kayttajat.nae_tukipyyntolista": "Käyttäjän tukipyyntöjen tilat (luku)",
        "kayttajat.muokkaa_perustietoja": "Käyttäjän perustietojen muokkaus",
        "kayttajat.lukitse_tunnus": "Lukitse käyttäjätunnus",
        "kayttajat.vapauta_tunnus_lukitus": "Vapauta käyttäjätunnuksen lukitus",
        "kayttajat.vapauta_ip_lukitus": "Vapauta ID-proofing-lukitus",
        "kayttajat.todenna_manuaalisesti":
          "Todenna henkilöllisyys manuaalisesti",
        "kayttajat.taikanappi": "Ohita ID-proofing (poikkeustoiminto)",
        "kayttajat.poista": "Poista käyttäjä",
        "kayttajat.audit_trail": "Virkailijan toimenpiteet -loki",
        "kayttajat.tapahtumat_yritykset": "Tapahtumat + todentamisyritykset",
        "raportit.tukipyyntoraportti": "Tukipyyntöraportti",
        "raportit.kayttoraportti": "Käyttöraportti",
        "raportit.kaikkien_kasittelijat": "Käsittelijöiden vertailutaulukko",
        "kayttooikeudet.myonna": "Käyttöoikeuksien myöntäminen",
        jonossa_tagi: '"Tukipyyntöjä jonossa" -tagi',
      }
) as { [K in Toiminto]: string };

// ---------------------------------------------------------------------------
// Perustyo-tasot — kumulatiiviset oikeusjoukot
// ---------------------------------------------------------------------------

/* Puhelin-aspa: käyttäjähaku + perustiedot + jonossa-tagi + käyttäjän
   tukipyyntöjen tilat (G2, read-only). Ei pääsyä itse tukipyyntöihin. */
const ASPA_TOIMINNOT: Toiminto[] = [
  "navi.kayttajahaku",
  "kayttajat.avaa_kortti",
  "kayttajat.nae_tukipyyntolista",
  "jonossa_tagi",
];

/* Tukipyyntöjen peruskatselu — ennen oma "katselija"-taso; 22.6.2026 (D4)
   poistettu erillisenä roolina ja sulautettu käsittelijään. 
   Lisätään myöhemmin tämä: 
   "kayttajat.lue_muistiinpanot",
   */
const TUKIPYYNTO_PERUS_TOIMINNOT: Toiminto[] = [
  "navi.tukipyynnot",
  "tukipyynnot.avaa_yksittainen",
];

/* Tukipyyntöjen käsittelijä: peruskatselu + toimet + tapahtumat/audit (K5 OSA 2.3). 
lisätään myöhemmin tämä:
"kayttajat.muokkaa_muistiinpanoja",
*/
const KASITTELIJA_LISATOIMINNOT: Toiminto[] = [
  "tukipyynnot.kasittele",
  "kayttajat.muokkaa_perustietoja",
  "kayttajat.vapauta_ip_lukitus",
  "kayttajat.todenna_manuaalisesti", // 24.6.2026: myös käsittelijälle — D9:n "(admin)" oli yhden roolin ajalta (20.5.); käsittelijä saa jo riskialttiimman taikanapin, joten normaali manuaaliprosessi kuuluu sille johdonmukaisesti
  "kayttajat.taikanappi", // K5: myös käsittelijälle (3g)
  "kayttajat.audit_trail", // K5 OSA 2.3: käsittelijälle näkyviin
  "kayttajat.tapahtumat_yritykset", // K5 OSA 2.3: käsittelijälle näkyviin
];

/* Admin: käsittelijä + raskaat toimet + lisäoikeudet sisäänrakennettuna. */
const ADMIN_LISATOIMINNOT: Toiminto[] = [
  "kayttajat.lukitse_tunnus",
  "kayttajat.vapauta_tunnus_lukitus",
  "kayttajat.todenna_manuaalisesti",
  "kayttajat.poista",
  // Admin saa myös raportit + myöntö automaattisesti (käsitellään voiNahda:ssa)
];

/* Yleisraportit — käsittelijän perustasolla (22.6.2026: näkee raportit ja
   omat käsittelymääränsä, muttei muiden). */
const RAPORTTI_PERUS: Toiminto[] = piilotaRaportit
  ? []
  : ["navi.raportit", "raportit.tukipyyntoraportti", "raportit.kayttoraportti"];

/* Täysi raporttioikeus (lisäoikeus tai admin): yleisraportit + kaikkien
   käsittelijöiden vertailumäärät. */
const RAPORTTI_TOIMINNOT: Toiminto[] = piilotaRaportit
  ? []
  : [...RAPORTTI_PERUS, "raportit.kaikkien_kasittelijat"];

/* Myöntöoikeudet (lisäoikeus tai admin). */
const MYONTO_TOIMINNOT: Toiminto[] = [
  "navi.kayttooikeudet",
  "kayttooikeudet.myonna",
];

function perustyon_oikeudet(taso: Perustyo): Set<Toiminto> {
  switch (taso) {
    case "ei":
      return new Set<Toiminto>();
    case "aspa":
      return new Set<Toiminto>(ASPA_TOIMINNOT);
    case "kasittelija":
      return new Set<Toiminto>([
        ...ASPA_TOIMINNOT,
        ...TUKIPYYNTO_PERUS_TOIMINNOT,
        ...KASITTELIJA_LISATOIMINNOT,
        ...(piilotaRaportit ? [] : RAPORTTI_PERUS),
      ]);
    case "admin": {
      // Admin = kaikki Toiminnot
      const kaikki = Object.keys(TOIMINTO_NIMET).filter(
        (t) => !piilotaRaportit || !t.startsWith("raportit."),
      ) as Toiminto[];
      return new Set<Toiminto>(kaikki);
    }
  }
}

// ---------------------------------------------------------------------------
// Pääfunktiot
// ---------------------------------------------------------------------------

/** Voiko käyttäjä tehdä tietyn toiminnon profiilillaan?
 *  Laskee union: perustyön oikeudet ∪ lisäoikeuksien oikeudet.
 *  Admin on superset kaikesta — ei erillistä tarkistusta lisäoikeuksille. */
export function voiNahda(
  toiminto: Toiminto,
  profiili: RooliProfiili | undefined,
): boolean {
  // Ei profiilia → ei oikeuksia
  if (!profiili) return false;
  // Admin: kaikki oikeudet
  if (profiili.perustyo === "admin") return true;
  // Perustyo
  if (perustyon_oikeudet(profiili.perustyo).has(toiminto)) return true;
  // Lisäoikeudet
  if (
    profiili.lisaoikeudet.raportit &&
    !piilotaRaportit &&
    RAPORTTI_TOIMINNOT.includes(toiminto)
  ) {
    return true;
  }
  if (profiili.lisaoikeudet.myonto && MYONTO_TOIMINNOT.includes(toiminto))
    return true;
  return false;
}

export function voiKasitella(profiili: RooliProfiili | undefined): boolean {
  return voiNahda("tukipyynnot.kasittele", profiili);
}

/** Saako rooli käyttää vain täsmähakua (tarkka UID tai sähköposti), ei
 *  selailtavaa nimi-/osahakua? G1/F4 (22.6.2026): puhelinaspalla aikapaine →
 *  iso riski katsoa väärää UID:tä, siksi vain tarkka haku. Tunnistus: rooli
 *  näkee käyttäjähaun muttei pääse yksittäiseen tukipyyntöön (= aspa-taso;
 *  käsittelijä ja admin saavat vapaan haun). */
export function vainTasmaHaku(profiili: RooliProfiili | undefined): boolean {
  return (
    voiNahda("navi.kayttajahaku", profiili) &&
    !voiNahda("tukipyynnot.avaa_yksittainen", profiili)
  );
}

/** Efektiivinen pääsy — jaettu helper DemoBannerille ja myöntönäkymälle.
 *  Yksi totuuslähde: sama laskenta molemmissa (K5 S4 + myöntönäkymä §3.3). */
export function laskeEfektiivinenPaasy(profiili: RooliProfiili | undefined): {
  kaytossa: Toiminto[];
  piilossa: Toiminto[];
} {
  const kaikki = Object.keys(TOIMINTO_NIMET) as Toiminto[];
  const kaytossa = kaikki.filter((t) => voiNahda(t, profiili));
  const piilossa = kaikki.filter((t) => !voiNahda(t, profiili));
  return { kaytossa, piilossa };
}

/** Piilotettujen toimintojen lukumäärä. */
export function piilotettujenMaara(profiili: RooliProfiili): number {
  return laskeEfektiivinenPaasy(profiili).piilossa.length;
}

// ---------------------------------------------------------------------------
// Demo-profiilit (proton roolin­valitsin)
// ---------------------------------------------------------------------------

export interface DemoProfiili {
  id: string;
  nimi: string;
  lyhytKuvaus: string;
  profiili: RooliProfiili;
}

export const DEMO_PROFIILIT: DemoProfiili[] = !piilotaRaportit
  ? [
      {
        id: "admin",
        nimi: "Admin",
        lyhytKuvaus:
          "Kaikki oikeudet. Palvelupisteen 2. asteen tuki + järjestelmän kokonaistila.",
        profiili: {
          perustyo: "admin",
          lisaoikeudet: { raportit: false, myonto: false },
        },
      },
      {
        id: "kasittelija",
        nimi: "Tukipyyntöjen käsittelijä",
        lyhytKuvaus:
          "Päivittäinen tukipyyntötyö. Tapahtumat, yritykset ja audit trail näkyvissä (K5).",
        profiili: {
          perustyo: "kasittelija",
          lisaoikeudet: { raportit: false, myonto: false },
        },
      },
      {
        id: "aspa",
        nimi: "Puhelin-aspa",
        lyhytKuvaus:
          "Käyttäjähaku + perustiedot + jonossa-tagi. Ei tukipyyntöjä eikä kuvia.",
        profiili: {
          perustyo: "aspa",
          lisaoikeudet: { raportit: false, myonto: false },
        },
      },
      {
        id: "esihenkilo",
        nimi: "Esihenkilö / Raporttien katselija",
        lyhytKuvaus: "Vain raportit. Ei tukipyyntöjä eikä käyttäjäkortteja.",
        profiili: {
          perustyo: "ei",
          lisaoikeudet: { raportit: true, myonto: false },
        },
      },
      {
        id: "myontaja",
        nimi: "Käyttöoikeuksien myöntäjä",
        lyhytKuvaus:
          "Pelkkä oikeuksien myöntäminen. Ei käyttäjätietoja eikä tukipyyntöjä.",
        profiili: {
          perustyo: "ei",
          lisaoikeudet: { raportit: false, myonto: true },
        },
      },
      {
        id: "kasittelija_myonto",
        nimi: "Käsittelijä + oikeuksien myöntö",
        lyhytKuvaus:
          "Tukipyyntötyö ja oikeuksien myöntäminen. Bea:n tiimin tyypillinen profiili.",
        profiili: {
          perustyo: "kasittelija",
          lisaoikeudet: { raportit: false, myonto: true },
        },
      },
      {
        id: "kasittelija_raportit",
        nimi: "Käsittelijä + raportit",
        lyhytKuvaus: "Tukipyyntötyö ja täysi raporttien näkyvyys.",
        profiili: {
          perustyo: "kasittelija",
          lisaoikeudet: { raportit: true, myonto: false },
        },
      },
      {
        id: "kasittelija_myonto_raportit",
        nimi: "Käsittelijä + oikeuksien myöntö + raportit",
        lyhytKuvaus:
          "Tukipyyntötyö ja oikeuksien myöntäminen. Lisäksi täysi raporttien näkyvyys",
        profiili: {
          perustyo: "kasittelija",
          lisaoikeudet: { raportit: true, myonto: true },
        },
      },
      {
        id: "aspa_myonto",
        nimi: "Puhelinaspa + oikeuksien myöntö",
        lyhytKuvaus: "Puhelinaspatyö ja oikeuksien myöntäminen.",
        profiili: {
          perustyo: "aspa",
          lisaoikeudet: { raportit: false, myonto: true },
        },
      },
      {
        id: "aspa_raportit",
        nimi: "Puhelinaspa + raportit",
        lyhytKuvaus: "Puhelinaspatyö. Lisäksi täysi raporttien näkyvyys.",
        profiili: {
          perustyo: "aspa",
          lisaoikeudet: { raportit: true, myonto: false },
        },
      },
      {
        id: "aspa_myonto_raportit",
        nimi: "Puhelinaspatyö + oikeuksien myöntö + raportit",
        lyhytKuvaus:
          "Puhelinaspatyö ja oikeuksien myöntäminen. Lisäksi täysi raporttien näkyvyys.",
        profiili: {
          perustyo: "aspa",
          lisaoikeudet: { raportit: true, myonto: true },
        },
      },
    ]
  : [
      {
        id: "admin",
        nimi: "Admin",
        lyhytKuvaus:
          "Kaikki oikeudet. Palvelupisteen 2. asteen tuki + järjestelmän kokonaistila.",
        profiili: {
          perustyo: "admin",
          lisaoikeudet: { raportit: false, myonto: false },
        },
      },
      {
        id: "kasittelija",
        nimi: "Tukipyyntöjen käsittelijä",
        lyhytKuvaus:
          "Päivittäinen tukipyyntötyö. Tapahtumat, yritykset ja audit trail näkyvissä (K5).",
        profiili: {
          perustyo: "kasittelija",
          lisaoikeudet: { raportit: false, myonto: false },
        },
      },
      {
        id: "aspa",
        nimi: "Puhelin-aspa",
        lyhytKuvaus:
          "Käyttäjähaku + perustiedot + jonossa-tagi. Ei tukipyyntöjä eikä kuvia.",
        profiili: {
          perustyo: "aspa",
          lisaoikeudet: { raportit: false, myonto: false },
        },
      },
      {
        id: "myontaja",
        nimi: "Käyttöoikeuksien myöntäjä",
        lyhytKuvaus:
          "Pelkkä oikeuksien myöntäminen. Ei käyttäjätietoja eikä tukipyyntöjä.",
        profiili: {
          perustyo: "ei",
          lisaoikeudet: { raportit: false, myonto: true },
        },
      },
      {
        id: "kasittelija_myonto",
        nimi: "Käsittelijä + oikeuksien myöntö",
        lyhytKuvaus:
          "Tukipyyntötyö ja oikeuksien myöntäminen. Bea:n tiimin tyypillinen profiili.",
        profiili: {
          perustyo: "kasittelija",
          lisaoikeudet: { raportit: false, myonto: true },
        },
      },
      {
        id: "aspa_myonto",
        nimi: "Puhelinaspa + oikeuksien myöntö",
        lyhytKuvaus: "Puhelinaspatyö ja oikeuksien myöntäminen.",
        profiili: {
          perustyo: "aspa",
          lisaoikeudet: { raportit: false, myonto: true },
        },
      },
    ];

/** Etsi demo-profiili id:n perusteella. */
export function haeDemoProfiili(id: string): DemoProfiili | undefined {
  return DEMO_PROFIILIT.find((p) => p.id === id);
}

/** Tunnista demo-profiili profiilin rakenteen perusteella (sessio­storage → demo-valitsin). */
export function tunnistaDemoProfiili(
  profiili: RooliProfiili,
): DemoProfiili | undefined {
  return DEMO_PROFIILIT.find(
    (dp) =>
      dp.profiili.perustyo === profiili.perustyo &&
      dp.profiili.lisaoikeudet.raportit === profiili.lisaoikeudet.raportit &&
      dp.profiili.lisaoikeudet.myonto === profiili.lisaoikeudet.myonto,
  );
}

// oikeudet.ts
export const ADMIN_GROUP_ID = import.meta.env.VITE_ADMIN_GROUP_ID;
export const HANDLER_GROUP_ID = import.meta.env.VITE_HANDLER_GROUP_ID;
export const ASPA_GROUP_ID = import.meta.env.VITE_ASPA_GROUP_ID;
export const REPORTS_GROUP_ID = import.meta.env.VITE_REPORTS_GROUP_ID;
export const RIGHTS_GROUP_ID = import.meta.env.VITE_RIGHTS_GROUP_ID;
export const ID_PROOFING_MAX = import.meta.env.VITE_USERS_ID_PROOFING_MAX;

export function profiiliToGroupIds(profiili: RooliProfiili): string[] {
  const groups: string[] = [];

  // Base work (perustyo) → primary group
  switch (profiili.perustyo) {
    case "admin":
      groups.push(ADMIN_GROUP_ID);
      break;
    case "kasittelija":
      groups.push(HANDLER_GROUP_ID);
      break;
    case "aspa":
      groups.push(ASPA_GROUP_ID);
      break;
    // "ei" → no base group
  }

  // Additional rights (only relevant when NOT admin, since admin includes all)
  if (profiili.perustyo !== "admin") {
    if (profiili.lisaoikeudet.raportit && !piilotaRaportit) {
      groups.push(REPORTS_GROUP_ID);
    }
    if (profiili.lisaoikeudet.myonto) {
      groups.push(RIGHTS_GROUP_ID);
    }
  }

  return groups;
}

export function annaProfiiliTrivoreKayttajalle(
  kayttaja: { memberOf?: string[] } | undefined,
): DemoProfiili | undefined {
  if (kayttaja?.memberOf?.includes(ADMIN_GROUP_ID)) {
    return DEMO_PROFIILIT.find((p) => p.id === "admin");
  }
  if (kayttaja?.memberOf?.includes(HANDLER_GROUP_ID)) {
    {
      if (kayttaja?.memberOf?.includes(RIGHTS_GROUP_ID)) {
        if (
          !piilotaRaportit &&
          kayttaja?.memberOf?.includes(REPORTS_GROUP_ID)
        ) {
          return DEMO_PROFIILIT.find(
            (profiili) => profiili.id === "kasittelija_myonto_raportit",
          );
        }
        return DEMO_PROFIILIT.find(
          (profiili) => profiili.id === "kasittelija_myonto",
        );
      }
      if (!piilotaRaportit && kayttaja?.memberOf?.includes(REPORTS_GROUP_ID)) {
        return DEMO_PROFIILIT.find(
          (profiili) => profiili.id === "kasittelija_raportit",
        );
      }
      return DEMO_PROFIILIT.find((profiili) => profiili.id === "kasittelija");
    }
  } else if (kayttaja?.memberOf?.includes(ASPA_GROUP_ID)) {
    if (kayttaja?.memberOf?.includes(RIGHTS_GROUP_ID)) {
      if (!piilotaRaportit && kayttaja?.memberOf?.includes(REPORTS_GROUP_ID)) {
        return DEMO_PROFIILIT.find(
          (profiili) => profiili.id === "aspa_myonto_raportit",
        );
      }
      return DEMO_PROFIILIT.find((profiili) => profiili.id === "aspa_myonto");
    }
    if (!piilotaRaportit && kayttaja?.memberOf?.includes(REPORTS_GROUP_ID)) {
      return DEMO_PROFIILIT.find((profiili) => profiili.id === "aspa_raportit");
    }
    return DEMO_PROFIILIT.find((profiili) => profiili.id === "aspa");
  } else if (kayttaja?.memberOf?.includes(RIGHTS_GROUP_ID)) {
    return DEMO_PROFIILIT.find((profiili) => profiili.id === "myontaja");
  } else if (!piilotaRaportit && kayttaja?.memberOf?.includes(REPORTS_GROUP_ID))
    return DEMO_PROFIILIT.find((profiili) => profiili.id === "esihenkilo");
}

const PERUSTYO_NIMET: Record<Perustyo, string> = {
  ei: "Ei perustyötä",
  aspa: "Puhelin-aspa",
  kasittelija: "Tukipyyntöjen käsittelijä",
  admin: "Admin",
};

/** Perustyö-vaihtoehdot radio-valintaan (nimi + kuvaus). Jaettu lähde
 *  myöntökortille (KayttooikeusKortti) ja virkailijan lisäyslomakkeelle
 *  (VirkailijanLisays), jotta roolikuvaukset pysyvät synkronassa. */
export const PERUSTYO_VAIHTOEHDOT: {
  arvo: Perustyo;
  nimi: string;
  kuvaus: string;
}[] = [
  {
    arvo: "ei",
    nimi: "Ei perustyötä",
    kuvaus: "Ei pääsyä tukipyyntöihin tai käyttäjäkortteihin.",
  },
  {
    arvo: "aspa",
    nimi: "Puhelin-aspa",
    kuvaus: "Käyttäjähaku + perustiedot + jonossa-tagi. Ei sisältöä.",
  },
  {
    arvo: "kasittelija",
    nimi: "Tukipyyntöjen käsittelijä",
    kuvaus:
      "Näkee ja käsittelee tukipyynnöt (ota itselle, ratkaise, muokkaa tietoja)",
  },
  {
    arvo: "admin",
    nimi: "Admin",
    kuvaus: "Kaikki oikeudet, ml. lisäoikeudet. Sisältää kaiken muun.",
  },
];

/** Ihmisluettava roolikuvaus profiilista — käytetään virkailijalistan
 *  rooli-sarakkeessa kun profiili tallennetaan myöntökortilta. */
export function kuvaaProfiili(profiili: RooliProfiili): string {
  if (profiili.perustyo === "admin") return "Admin";
  const osat: string[] = [PERUSTYO_NIMET[profiili.perustyo]];
  if (profiili.lisaoikeudet.raportit)
    osat.push("Täysi raporttien katseluoikeus");
  if (profiili.lisaoikeudet.myonto) osat.push("Oikeuksien myöntö");
  // Jos perustyo='ei' ja vain lisäoikeus, näytä lisäoikeus pääroolina
  if (profiili.perustyo === "ei" && osat.length > 1) {
    return osat.slice(1).join(" + ");
  }
  return osat.join(" + ");
}

// ---------------------------------------------------------------------------
// Valmisprofiilit (helpottamaan muuta koodia)
// ---------------------------------------------------------------------------

export const ADMIN_PROFIILI: RooliProfiili = {
  perustyo: "admin",
  lisaoikeudet: { raportit: false, myonto: false },
};
